import { Camera } from 'lucide-react';
import { usePhotoMeta, usePlace } from '../../hooks/usePhotoInfo';
import { useNowShowing } from '../../store/nowShowing';
import { useSession } from '../../store/session';
import { useSettings } from '../../store/settings';
import { TileMap } from './TileMap';

/**
 * Camera wall-clock time ("2019-07-14T10:22:31", any zone suffix ignored) as text. Cameras
 * record local time without a zone, and OneDrive reports it as if it were UTC, so it is
 * formatted in UTC to show the time on the camera's clock wherever the screen is.
 */
export function formatTaken(raw: string | null | undefined, showDate: boolean, showTime: boolean, hour12: boolean): string | null {
  const m = raw?.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m || (!showDate && !showTime)) return null;
  const d = new Date(Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!, +m[4]!, +m[5]!));
  if (Number.isNaN(d.getTime()) || d.getUTCFullYear() < 1900) return null;
  const parts: string[] = [];
  if (showDate) parts.push(new Intl.DateTimeFormat(undefined, { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(d));
  if (showTime) parts.push(new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit', hour12, timeZone: 'UTC' }).format(d));
  return parts.join(' · ');
}

/** Town, district, country, skipping repeats ("Madrid, Madrid, Spain" → "Madrid, Spain"). */
export function placeLine(names: Array<string | null | false | undefined>): string {
  const out: string[] = [];
  for (const n of names) if (n && !out.some((o) => o.toLowerCase() === n.toLowerCase())) out.push(n);
  return out.join(', ');
}

/** Where and when the photo on screen was taken, with a map. Renders nothing for photos without that information. */
export function PhotoInfo({ className = '', fill = false }: { className?: string; /** Fill a fixed-size box: the map takes the remaining height. */ fill?: boolean }) {
  const opts = useSettings((s) => s.photoInfo);
  const hour12 = useSettings((s) => s.clock.hour12);
  const { item, cycle, startedAt, durationSec } = useNowShowing();
  const mapConfig = useSession((s) => s.config?.map);
  const map = mapConfig?.styles?.find((m) => m.id === opts.mapStyle) ?? mapConfig;
  const meta = usePhotoMeta(item);

  const lat = meta?.latitude ?? null;
  const lon = meta?.longitude ?? null;
  const hasPosition = lat !== null && lon !== null;
  const wantsPlace = opts.showCity || opts.showDistrict || opts.showCountry;
  const place = usePlace(hasPosition && wantsPlace ? lat : null, hasPosition && wantsPlace ? lon : null);

  const where = place ? placeLine([opts.showCity && place.city, opts.showDistrict && place.district, opts.showCountry && place.country]) : '';
  const when = formatTaken(meta?.takenAt, opts.showDate, opts.showTime, hour12);
  const showMap = opts.showMap && hasPosition && map;

  if (!where && !when && !showMap) return null;

  return (
    // Keyed by slide so each photo's details fade in fresh and the map zoom restarts.
    <div key={cycle} className={`glass photo-info overflow-hidden ${fill ? 'flex flex-col' : ''} ${className}`}>
      {(where || when) && (
        <div className="flex items-start gap-3 px-4 py-3">
          <Camera className="mt-0.5 h-5 w-5 shrink-0 opacity-80" aria-hidden />
          <div className="min-w-0">
            {where && <div className="font-heading font-medium leading-snug">{where}</div>}
            {when && <div className="muted text-sm">{when}</div>}
          </div>
        </div>
      )}
      {showMap && (
        <div className={fill ? 'min-h-0 w-full flex-1' : 'aspect-[16/9] max-h-[18rem] w-full'}>
          <TileMap
            lat={lat}
            lon={lon}
            zoomFrom={opts.zoomMode === 'gradual' ? opts.zoomFrom : opts.zoom}
            zoomTo={opts.zoom}
            durationSec={durationSec}
            startedAt={startedAt}
            template={map.tileUrl}
            attribution={map.attribution}
            maxZoom={map.maxZoom}
            dark={opts.mapDark}
          />
        </div>
      )}
    </div>
  );
}
